/**
 * HISI-FE-AUTH-01: Cookie-based Authentication Hook
 *
 * Cookie-based authentication using server-side endpoints with credentials:include
 * No localStorage tokens or Authorization headers
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { joinApiPath } from '@/lib/api-config';
import { markAuthReady, markAuthCheckComplete } from '@/lib/auth-readiness';
import { getApiHeadersGet, getApiHeadersJson } from '@/lib/api-base';
import { getDemoError, type DemoError } from '@/lib/demo-error-handler';

export interface AuthUser {
  id: string;
  email: string;
  role?: string;
  isAdmin: boolean;
}

export interface AuthSession {
  user: AuthUser;
  // No token fields - using HttpOnly cookies
}

// DV-AUTH-004: Session metrics interface
export interface SessionMetrics {
  sessionChecks: {
    total: number;
    successful: number;
    failed: number;
    averageLatency: number;
    lastCheckAt?: string;
    lastSuccessAt?: string;
    lastFailureAt?: string;
  };
  refreshOperations: {
    total: number;
    successful: number;
    failed: number;
    averageLatency: number;
    lastRefreshAt?: string;
    lastSuccessAt?: string;
    lastFailureAt?: string;
  };
  recentLatencies: {
    sessionChecks: number[];
    refreshOperations: number[];
  };
}

export function useServerAuth() {
  if (import.meta.env.DEV) {
    console.debug('🔄 useServerAuth: Hook called'); // DV-AUTH-004: Debug level for verbose logs
  }
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [error, setError] = useState<DemoError | null>(null); // DV-AUTH-004: Unified error handling
  const checkAuthInFlightRef = useRef(false);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null); // DV-AUTH-004: Singleflight refresh
  const abortControllerRef = useRef<AbortController | null>(null); // DV-AUTH-004: Abort controller hygiene
  const refreshAbortControllerRef = useRef<AbortController | null>(null); // DV-AUTH-004: Separate abort controller for refresh

  // DV-AUTH-004: Session metrics tracking
  const metricsRef = useRef<SessionMetrics>({
    sessionChecks: {
      total: 0,
      successful: 0,
      failed: 0,
      averageLatency: 0
    },
    refreshOperations: {
      total: 0,
      successful: 0,
      failed: 0,
      averageLatency: 0
    },
    recentLatencies: {
      sessionChecks: [],
      refreshOperations: []
    }
  });

  // DV-AUTH-004: Helper functions for metrics tracking
  const updateSessionCheckMetrics = useCallback((elapsed: number, success: boolean) => {
    const metrics = metricsRef.current;
    const timestamp = new Date().toISOString();

    metrics.sessionChecks.total++;
    metrics.sessionChecks.lastCheckAt = timestamp;

    if (success) {
      metrics.sessionChecks.successful++;
      metrics.sessionChecks.lastSuccessAt = timestamp;
    } else {
      metrics.sessionChecks.failed++;
      metrics.sessionChecks.lastFailureAt = timestamp;
    }

    // Update latency tracking (keep last 20 measurements)
    metrics.recentLatencies.sessionChecks.push(elapsed);
    if (metrics.recentLatencies.sessionChecks.length > 20) {
      metrics.recentLatencies.sessionChecks.shift();
    }

    // Calculate average latency
    const totalLatency = metrics.recentLatencies.sessionChecks.reduce((sum, lat) => sum + lat, 0);
    metrics.sessionChecks.averageLatency = Math.round(totalLatency / metrics.recentLatencies.sessionChecks.length);
  }, []);

  const updateRefreshMetrics = useCallback((elapsed: number, success: boolean) => {
    const metrics = metricsRef.current;
    const timestamp = new Date().toISOString();

    metrics.refreshOperations.total++;
    metrics.refreshOperations.lastRefreshAt = timestamp;

    if (success) {
      metrics.refreshOperations.successful++;
      metrics.refreshOperations.lastSuccessAt = timestamp;
    } else {
      metrics.refreshOperations.failed++;
      metrics.refreshOperations.lastFailureAt = timestamp;
    }

    // Update latency tracking (keep last 20 measurements)
    metrics.recentLatencies.refreshOperations.push(elapsed);
    if (metrics.recentLatencies.refreshOperations.length > 20) {
      metrics.recentLatencies.refreshOperations.shift();
    }

    // Calculate average latency
    const totalLatency = metrics.recentLatencies.refreshOperations.reduce((sum, lat) => sum + lat, 0);
    metrics.refreshOperations.averageLatency = Math.round(totalLatency / metrics.recentLatencies.refreshOperations.length);
  }, []);

  /**
   * Check current authentication session via cookies
   */
  const checkAuthSession = useCallback(async () => {
    // Prevent duplicate requests
    if (checkAuthInFlightRef.current) {
      if (import.meta.env.DEV) {
        console.debug('🔑 Auth Hook: Session check already in progress, skipping duplicate request'); // DV-AUTH-004: Debug level for duplicate prevention
      }
      return;
    }

    checkAuthInFlightRef.current = true;

    // DV-AUTH-004: Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const requestId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const startTime = Date.now();

    // DV-AUTH-004: INFO level for important auth operations
    console.info('🔑 Auth Hook: Starting session check...', {
      requestId,
      timestamp: new Date().toISOString()
    });

    if (import.meta.env.DEV) {
      console.debug('🔑 Auth Hook: Session check details', {
        requestId,
        url: joinApiPath('auth', 'session')
      });
    }

    // Clear any previous errors
    setError(null);

    try {
      const response = await fetch(joinApiPath('auth', 'session'), {
        method: 'GET',
        credentials: 'include', // HISI-FE-AUTH-01: Cookie-based auth
        headers: getApiHeadersGet(), // DV-AUTH-004: Preflight-safe headers for GET requests
        signal: abortControllerRef.current.signal, // DV-AUTH-004: Abort signal hygiene
      });

      const elapsed = Date.now() - startTime;

      if (import.meta.env.DEV) {
        console.debug('🔑 Auth Hook: Session check response received:', {
          requestId,
          status: response.status,
          statusText: response.statusText,
          elapsed: `${elapsed}ms`,
          hasSetCookieHeader: !!response.headers.get('set-cookie')
        });
      }

      if (response.ok) {
        const data = await response.json();

        if (data.user) {
          // DV-AUTH-004: INFO level for successful authentication
          console.info('🔑 Auth Hook: Cookie session valid ✅', {
            requestId,
            userId: data.user.id,
            elapsed: `${elapsed}ms`
          });

          if (import.meta.env.DEV) {
            console.debug('🔑 Auth Hook: Authenticated user details', {
              requestId,
              email: data.user.email,
              role: data.user.role,
              isAdmin: data.user.isAdmin
            });
          }

          setUser(data.user);
          setSession({ user: data.user });
          markAuthReady(); // DV-AUTH-004: Mark auth ready for authenticated user
          updateSessionCheckMetrics(elapsed, true); // DV-AUTH-004: Track successful session check
        } else {
          // DV-AUTH-004: INFO level for anonymous user (important state)
          console.info('🔑 Auth Hook: No authenticated user in response', {
            requestId,
            elapsed: `${elapsed}ms`
          });

          if (import.meta.env.DEV) {
            console.debug('🔑 Auth Hook: Anonymous user details', {
              requestId,
              responseData: data
            });
          }

          setUser(null);
          setSession(null);
          markAuthCheckComplete(); // DV-AUTH-004: Mark check complete for anonymous user
          updateSessionCheckMetrics(elapsed, true); // DV-AUTH-004: Track successful session check (anonymous user)
        }
      } else if (response.status === 401) {
        // DV-AUTH-004: INFO level for session expiration (important state change)
        console.info('🔑 Auth Hook: Session invalid/expired ❌', {
          requestId,
          status: 401,
          elapsed: `${elapsed}ms`
        });

        if (import.meta.env.DEV) {
          console.debug('🔑 Auth Hook: Session expiration details', {
            requestId,
            timestamp: new Date().toISOString(),
            statusText: response.statusText
          });
        }

        setUser(null);
        setSession(null);
        markAuthCheckComplete(); // DV-AUTH-004: Mark check complete for expired session
        updateSessionCheckMetrics(elapsed, false); // DV-AUTH-004: Track failed session check (401)
      } else {
        console.warn('🔑 Auth Hook: Session check failed with unexpected status ⚠️', {
          requestId,
          status: response.status,
          statusText: response.statusText,
          elapsed: `${elapsed}ms`
        });

        // DV-AUTH-004: Create structured error for unexpected status
        const demoError = getDemoError({
          message: `Session check failed with status ${response.status}`,
          status: response.status,
          code: `AUTH_SESSION_ERROR_${response.status}`,
          details: { requestId, statusText: response.statusText }
        });

        if (demoError) {
          setError(demoError);
        }

        setUser(null);
        setSession(null);
        markAuthCheckComplete(); // DV-AUTH-004: Mark check complete for error cases
        updateSessionCheckMetrics(elapsed, false); // DV-AUTH-004: Track failed session check (unexpected status)
      }
    } catch (error) {
      // DV-AUTH-004: Handle abort errors gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        if (import.meta.env.DEV) {
          console.debug('🔑 Auth Hook: Session check aborted (component unmounting or new request) ⏹️', {
            requestId,
            elapsed: `${Date.now() - startTime}ms`
          });
        }
        // Don't update state on abort - component is unmounting or new request started
        return;
      }

      console.error('🔑 Auth Hook: Session check network/parsing error ❌', {
        requestId,
        error: error instanceof Error ? error.message : String(error),
        elapsed: `${Date.now() - startTime}ms`
      });

      // DV-AUTH-004: Create structured error for network/parsing errors
      const demoError = getDemoError({
        message: error instanceof Error ? error.message : 'Network error during session check',
        status: 0, // Network error
        code: 'AUTH_NETWORK_ERROR',
        details: { requestId, errorType: 'network' }
      });

      if (demoError) {
        setError(demoError);
      }

      setUser(null);
      setSession(null);
      markAuthCheckComplete(); // DV-AUTH-004: Mark check complete for network errors
      updateSessionCheckMetrics(Date.now() - startTime, false); // DV-AUTH-004: Track failed session check (network error)
    } finally {
      // DV-AUTH-004: Only update state if request wasn't aborted
      if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        console.log('🔑 Auth Hook: Session check completed, updating loading state', {
          requestId,
          finalElapsed: `${Date.now() - startTime}ms`
        });
        setLoading(false);
        checkAuthInFlightRef.current = false;
        abortControllerRef.current = null; // Clear controller reference
      } else if (abortControllerRef.current?.signal.aborted) {
        console.log('🔑 Auth Hook: Session check was aborted, skipping state updates', {
          requestId,
          finalElapsed: `${Date.now() - startTime}ms`
        });
        // Reset the in-flight flag even for aborted requests
        checkAuthInFlightRef.current = false;
      }
    }
  }, []); // Empty deps - this function should only be recreated if dependencies change

  /**
   * Sign out by clearing server-side session
   * DV-AUTH-004: Enhanced with abort controller hygiene
   */
  const signOut = useCallback(async () => {
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 5000); // 5 second timeout for logout

    try {
      await fetch(joinApiPath('auth', 'logout'), {
        method: 'POST',
        credentials: 'include', // Clear HttpOnly cookies
        headers: getApiHeadersJson(), // DV-AUTH-004: JSON headers for POST requests
        signal: abortController.signal, // DV-AUTH-004: Abort signal hygiene
      });
      clearTimeout(timeoutId);
      console.log('🔑 Auth: Logout successful ✅');
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('🔑 Auth: Logout request timed out (proceeding with client-side logout)');
      } else {
        console.warn('🔑 Auth: Logout error (proceeding with client-side logout):', error);
      }
    } finally {
      // Always clear client state regardless of server response
      setUser(null);
      setSession(null);
    }
  }, []);

  /**
   * Refresh session (for when cookies are rotated)
   * DV-AUTH-004: Singleflight pattern - concurrent calls return same promise
   */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    // If refresh already in flight, return the existing promise
    if (refreshInFlightRef.current) {
      console.log('🔑 Auth: Refresh already in progress, returning existing promise');
      return refreshInFlightRef.current;
    }

    const requestId = `refresh-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const startTime = Date.now();

    // DV-AUTH-004: Abort any previous refresh request
    if (refreshAbortControllerRef.current) {
      refreshAbortControllerRef.current.abort();
    }
    refreshAbortControllerRef.current = new AbortController();
    const timeoutId = setTimeout(() => refreshAbortControllerRef.current?.abort(), 10000); // 10 second timeout for refresh

    console.log('🔑 Auth: Starting session refresh...', {
      requestId,
      timestamp: new Date().toISOString()
    });

    // Create and store the refresh promise
    const refreshPromise = (async (): Promise<boolean> => {
      try {
        const response = await fetch(joinApiPath('auth', 'refresh'), {
          method: 'POST',
          credentials: 'include', // Use refresh cookies
          headers: getApiHeadersJson(), // DV-AUTH-004: JSON headers for POST requests
          signal: refreshAbortControllerRef.current?.signal, // DV-AUTH-004: Abort signal hygiene
        });

        clearTimeout(timeoutId);

        const elapsed = Date.now() - startTime;
        console.log('🔑 Auth: Refresh response received:', {
          requestId,
          status: response.status,
          statusText: response.statusText,
          elapsed: `${elapsed}ms`
        });

        if (response.ok) {
          // Session refreshed, check new session
          await checkAuthSession();
          console.log('🔑 Auth: Refresh successful ✅', {
            requestId,
            elapsed: `${elapsed}ms`
          });
          updateRefreshMetrics(elapsed, true); // DV-AUTH-004: Track successful refresh
          return true;
        } else {
          console.log('🔑 Auth: Refresh failed ❌', {
            requestId,
            status: response.status,
            elapsed: `${elapsed}ms`
          });
          setUser(null);
          setSession(null);
          updateRefreshMetrics(elapsed, false); // DV-AUTH-004: Track failed refresh (server error)
          return false;
        }
      } catch (error) {
        clearTimeout(timeoutId);

        // DV-AUTH-004: Handle abort errors gracefully
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('🔑 Auth: Refresh request aborted (timeout or component unmounting) ⏹️', {
            requestId,
            elapsed: `${Date.now() - startTime}ms`
          });
          // Don't update auth state on abort - just return false
          return false;
        }

        console.error('🔑 Auth: Refresh network/parsing error ❌', {
          requestId,
          error: error instanceof Error ? error.message : String(error),
          elapsed: `${Date.now() - startTime}ms`
        });
        setUser(null);
        setSession(null);
        updateRefreshMetrics(Date.now() - startTime, false); // DV-AUTH-004: Track failed refresh (network error)
        return false;
      } finally {
        // Clear the in-flight reference and abort controller when done
        refreshInFlightRef.current = null;
        refreshAbortControllerRef.current = null; // DV-AUTH-004: Clear abort controller
        console.log('🔑 Auth: Refresh completed, cleared singleflight lock', {
          requestId,
          finalElapsed: `${Date.now() - startTime}ms`
        });
      }
    })();

    // Store the promise so concurrent calls can use it
    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [checkAuthSession]);

  // Initialize session check with StrictMode idempotence
  // DV-AUTH-004: In StrictMode, effects run twice but we already have protection
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.debug('🔄 useServerAuth: useEffect executed - calling checkAuthSession (StrictMode safe)'); // DV-AUTH-004: Debug level for lifecycle
    }
    checkAuthSession();
  }, [checkAuthSession]);

  // DV-AUTH-004: Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      if (import.meta.env.DEV) {
        console.debug('🔄 useServerAuth: Component unmounting, aborting in-flight requests'); // DV-AUTH-004: Debug level for cleanup
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (refreshAbortControllerRef.current) {
        refreshAbortControllerRef.current.abort();
        refreshAbortControllerRef.current = null;
      }
    };
  }, []);

  /**
   * Clear authentication error
   * DV-AUTH-004: Unified error handling
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get current session metrics
   * DV-AUTH-004: Expose metrics for status reporting
   */
  const getSessionMetrics = useCallback((): SessionMetrics => {
    return { ...metricsRef.current }; // Return a copy to prevent mutation
  }, []);

  return {
    user,
    session,
    loading,
    error, // DV-AUTH-004: Expose authentication errors
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    checkAuthSession,
    refreshSession,
    signOut,
    clearError, // DV-AUTH-004: Method to clear errors
    getSessionMetrics, // DV-AUTH-004: Expose session metrics
  };
}